import { userFacingError } from "../userFacingError";
import { connectorsByIdentifier } from "./index";
import {
  isManagedAiProviderConnector,
  MANAGED_AI_CONNECTOR_TYPE,
  managedAiConnectorConfiguration,
} from "./managedAi";
import {
  isTriviallyMatchingPattern,
  urlPatternError,
} from "./urlPatternConversion";

const MAX_URL_PATTERN_LENGTH = 2048;

function assertValidConnectorUrlPattern(urlPattern: string): void {
  const trimmed = urlPattern.trim();
  if (trimmed.length > MAX_URL_PATTERN_LENGTH) {
    throw userFacingError("URL pattern is too long.");
  }
  const error = urlPatternError(trimmed);
  if (error) throw userFacingError(error);
}

export function normalizeConnectorUrlPattern(
  urlPattern: string,
  identifier: string,
  configuration: Record<string, string>,
  previousConfiguration?: Record<string, string>
): string {
  const definition = connectorsByIdentifier[identifier];
  const derive = (config: Record<string, string>) => {
    const derived = definition?.defaultUrlPattern(config).trim() ?? "";
    return isTriviallyMatchingPattern(derived) ? "" : derived;
  };

  // A pattern still equal to the default for the previous configuration was
  // never customized, so it keeps tracking the default as the configuration
  // (e.g. the domain) changes.
  const trimmed = urlPattern.trim();
  const tracksDefault =
    !trimmed ||
    isTriviallyMatchingPattern(trimmed) ||
    (previousConfiguration !== undefined &&
      trimmed === derive(previousConfiguration));
  return tracksDefault ? derive(configuration) : trimmed;
}

// The validated, trimmed, normalized identity fields every connector write
// shares — one policy for create, update, and variant switch.
export function normalizedConnectorIdentity(fields: {
  name: string;
  identifier: string;
  urlPattern: string;
  configuration: Record<string, string>;
  previousConfiguration?: Record<string, string>;
}) {
  assertValidConnectorUrlPattern(fields.urlPattern);
  const identifier = fields.identifier.trim();
  // The per-provider managed connectors carry 3B’s deployment credentials, so a
  // row naming one would spend 3B’s provider accounts outside its controls.
  if (isManagedAiProviderConnector(identifier)) {
    throw userFacingError("Connectors managed by 3B can’t be created");
  }
  // 3B sets a managed AI row’s endpoint and trust, so neither may be customized.
  if (identifier === MANAGED_AI_CONNECTOR_TYPE.identifier) {
    const configuration = managedAiConnectorConfiguration(fields.configuration);
    return {
      name: fields.name.trim(),
      identifier,
      urlPattern: normalizeConnectorUrlPattern("", identifier, configuration),
      configuration,
    };
  }
  return {
    name: fields.name.trim(),
    identifier,
    urlPattern: normalizeConnectorUrlPattern(
      fields.urlPattern,
      identifier,
      fields.configuration,
      fields.previousConfiguration
    ),
    configuration: fields.configuration,
  };
}
